import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { HeatMapClick, HeatMapConfig } from "@/types/survey";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { 
  ToggleGroup, 
  ToggleGroupItem 
} from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Target, MousePointerClick, BarChart3, Users, User, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================
// Types
// ============================================

// Individual response data for individual view mode
export interface IndividualResponseData {
  responseId: number;  // Response ID for identification
  responseIndex: number;  // 1-based index for display
  clicks: HeatMapClick[];
  submittedAt?: string;  // Optional timestamp for display
}

interface HeatMapVisualizationProps {
  imageUrl: string;
  clicks: HeatMapClick[];  // All clicks from all responses (aggregate)
  config: HeatMapConfig;
  imageNaturalWidth: number;
  imageNaturalHeight: number;
  individualResponses?: IndividualResponseData[];  // For individual response view
}

type VisualizationMode = "heatmap" | "dots" | "regions";
type ViewMode = "aggregate" | "individual";

interface ControlState {
  radius: number;      // 10-100 (px equivalent at natural size)
  blur: number;        // 0-50
  intensity: number;   // 0-100 (opacity %)
}

// Default control values per guide spec
const DEFAULT_CONTROLS: ControlState = {
  radius: 30,
  blur: 15,
  intensity: 70,
};

// Color gradient: transparent → blue → green → yellow → red
const GRADIENT_COLORS = [
  { stop: 0.0, color: "rgba(0, 0, 255, 0)" },      // Transparent
  { stop: 0.2, color: "rgba(0, 0, 255, 0.5)" },    // Blue
  { stop: 0.4, color: "rgba(0, 255, 0, 0.6)" },    // Green
  { stop: 0.6, color: "rgba(255, 255, 0, 0.7)" },  // Yellow
  { stop: 0.8, color: "rgba(255, 128, 0, 0.8)" },  // Orange
  { stop: 1.0, color: "rgba(255, 0, 0, 0.9)" },    // Red
];

// ============================================
// Heat Map Visualization Component
// ============================================

export function HeatMapVisualization({
  imageUrl,
  clicks,
  config,
  imageNaturalWidth,
  imageNaturalHeight: _imageNaturalHeight,
  individualResponses = [],
}: HeatMapVisualizationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [mode, setMode] = useState<VisualizationMode>("heatmap");
  const [controls, setControls] = useState<ControlState>(DEFAULT_CONTROLS);
  
  // View mode: aggregate (all responses) vs individual (one response at a time)
  const [viewMode, setViewMode] = useState<ViewMode>("aggregate");
  const [selectedResponseIndex, setSelectedResponseIndex] = useState(0);  // Index into individualResponses array

  // Track image element for drawing
  const imageRef = useRef<HTMLImageElement | null>(null);
  
  // Determine which clicks to display based on view mode
  const displayClicks = useMemo(() => {
    if (viewMode === "aggregate") {
      return clicks;
    }
    // Individual mode: show clicks from selected response only
    if (individualResponses.length > 0 && selectedResponseIndex < individualResponses.length) {
      return individualResponses[selectedResponseIndex].clicks;
    }
    return [];
  }, [viewMode, clicks, individualResponses, selectedResponseIndex]);
  
  // Navigation helpers for individual view
  const canNavigatePrev = selectedResponseIndex > 0;
  const canNavigateNext = selectedResponseIndex < individualResponses.length - 1;
  
  const handlePrevResponse = useCallback(() => {
    if (canNavigatePrev) {
      setSelectedResponseIndex(prev => prev - 1);
    }
  }, [canNavigatePrev]);
  
  const handleNextResponse = useCallback(() => {
    if (canNavigateNext) {
      setSelectedResponseIndex(prev => prev + 1);
    }
  }, [canNavigateNext]);
  
  // Reset to first response when switching to individual mode
  useEffect(() => {
    if (viewMode === "individual" && individualResponses.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional reset on mode change
      setSelectedResponseIndex(0);
    }
  }, [viewMode, individualResponses.length]);
  
  // Current response info (for display)
  const currentResponse = viewMode === "individual" && individualResponses.length > 0
    ? individualResponses[selectedResponseIndex]
    : null;

  // Parse rgba string to object (moved above getColorForIntensity which calls it)
  const parseRgba = (rgba: string): { r: number; g: number; b: number; a: number } => {
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (!match) return { r: 0, g: 0, b: 0, a: 1 };
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
      a: match[4] ? parseFloat(match[4]) : 1,
    };
  };

  // Get color for a given intensity value (0-1) - wrapped in useCallback for hook deps
  const getColorForIntensity = useCallback(
    (intensity: number): { r: number; g: number; b: number; a: number } => {
      if (intensity === 0) return { r: 0, g: 0, b: 0, a: 0 };

      // Find the two color stops we're between
      for (let i = 0; i < GRADIENT_COLORS.length - 1; i++) {
        const curr = GRADIENT_COLORS[i];
        const next = GRADIENT_COLORS[i + 1];
        
        if (intensity >= curr.stop && intensity <= next.stop) {
          const t = (intensity - curr.stop) / (next.stop - curr.stop);
          const currRgba = parseRgba(curr.color);
          const nextRgba = parseRgba(next.color);
          
          return {
            r: Math.round(currRgba.r + (nextRgba.r - currRgba.r) * t),
            g: Math.round(currRgba.g + (nextRgba.g - currRgba.g) * t),
            b: Math.round(currRgba.b + (nextRgba.b - currRgba.b) * t),
            a: currRgba.a + (nextRgba.a - currRgba.a) * t,
          };
        }
      }

      // Fallback to last color
      const last = GRADIENT_COLORS[GRADIENT_COLORS.length - 1];
      return parseRgba(last.color);
    },
    [] // GRADIENT_COLORS is a module constant, parseRgba is stable
  );

  // Handle image load
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    imageRef.current = e.currentTarget;
    setImageLoaded(true);
  }, []);

  // Handle container resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Draw heat map on canvas
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imageRef.current) return;
    if (mode !== "heatmap" || displayClicks.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const img = imageRef.current;
    const displayWidth = img.clientWidth;
    const displayHeight = img.clientHeight;

    // Set canvas size to match displayed image
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scale factor for radius (based on display vs natural size)
    const scaleFactor = displayWidth / imageNaturalWidth;
    const scaledRadius = controls.radius * scaleFactor;

    // Create off-screen canvas for heat values
    const heatCanvas = document.createElement("canvas");
    heatCanvas.width = displayWidth;
    heatCanvas.height = displayHeight;
    const heatCtx = heatCanvas.getContext("2d");
    if (!heatCtx) return;

    // Draw heat points (grayscale intensity)
    displayClicks.forEach((click) => {
      const x = (click.xPct / 100) * displayWidth;
      const y = (click.yPct / 100) * displayHeight;

      // Create radial gradient for each point
      const gradient = heatCtx.createRadialGradient(x, y, 0, x, y, scaledRadius);
      gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

      heatCtx.fillStyle = gradient;
      heatCtx.beginPath();
      heatCtx.arc(x, y, scaledRadius, 0, Math.PI * 2);
      heatCtx.fill();
    });

    // Apply blur if specified
    if (controls.blur > 0) {
      ctx.filter = `blur(${controls.blur * scaleFactor}px)`;
    }

    // Draw the heat layer
    ctx.drawImage(heatCanvas, 0, 0);
    ctx.filter = "none";

    // Get the heat data and colorize it
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Find max intensity for normalization
    let maxIntensity = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] > maxIntensity) maxIntensity = data[i];
    }

    // Colorize based on intensity
    for (let i = 0; i < data.length; i += 4) {
      const intensity = data[i] / (maxIntensity || 1);
      const color = getColorForIntensity(intensity);
      
      data[i] = color.r;
      data[i + 1] = color.g;
      data[i + 2] = color.b;
      data[i + 3] = Math.round(color.a * (controls.intensity / 100) * 255);
    }

    ctx.putImageData(imageData, 0, 0);
  }, [imageLoaded, displayClicks, mode, controls, imageNaturalWidth, containerSize, getColorForIntensity]);

  // Calculate region click counts (uses displayClicks for current view)
  const getRegionCounts = useCallback((): Map<string, number> => {
    const counts = new Map<string, number>();
    
    if (!config.regions || config.regions.length === 0) {
      return counts;
    }

    // Initialize counts for all regions + "other"
    config.regions.forEach((region) => {
      counts.set(region.id, 0);
    });
    counts.set("other", 0);

    // Count clicks per region
    displayClicks.forEach((click) => {
      const regionId = click.regionId || "other";
      counts.set(regionId, (counts.get(regionId) || 0) + 1);
    });

    return counts;
  }, [config.regions, displayClicks]);

  // Get region name by ID
  const getRegionName = (regionId: string): string => {
    if (regionId === "other") return "Outside regions";
    const region = config.regions?.find((r) => r.id === regionId);
    return region?.name || regionId;
  };

  const regionCounts = getRegionCounts();
  const hasRegions = config.regions && config.regions.length > 0;
  const hasIndividualResponses = individualResponses.length > 0;

  return (
    <div className="space-y-4">
      {/* View Mode Toggle (Aggregate vs Individual) */}
      {hasIndividualResponses && (
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg border">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">View:</span>
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value) => value && setViewMode(value as ViewMode)}
              className="justify-start"
            >
              <ToggleGroupItem value="aggregate" aria-label="All responses" className="gap-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">All Responses</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="individual" aria-label="Individual response" className="gap-1">
                <User className="h-4 w-4" />
                <span className="text-xs">Individual</span>
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          
          {/* Response Navigator (only in individual mode) */}
          {viewMode === "individual" && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={handlePrevResponse}
                disabled={!canNavigatePrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              
              <Select
                value={String(selectedResponseIndex)}
                onValueChange={(value) => setSelectedResponseIndex(parseInt(value, 10))}
              >
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder="Select response" />
                </SelectTrigger>
                <SelectContent>
                  {individualResponses.map((resp, idx) => (
                    <SelectItem key={resp.responseId} value={String(idx)}>
                      Response #{resp.responseIndex}
                      {resp.submittedAt && (
                        <span className="text-xs text-muted-foreground ml-2">
                          {new Date(resp.submittedAt).toLocaleDateString()}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={handleNextResponse}
                disabled={!canNavigateNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Visualization Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Heat Map</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={mode === "dots" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("dots")}
          >
            <MousePointerClick className="h-4 w-4 mr-1" />
            Clicks
          </Button>
          <Button
            variant={mode === "heatmap" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("heatmap")}
          >
            <Target className="h-4 w-4 mr-1" />
            Heat
          </Button>
          {hasRegions && (
            <Button
              variant={mode === "regions" ? "default" : "outline"}
              size="sm"
              onClick={() => setMode("regions")}
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              Regions
            </Button>
          )}
        </div>
      </div>

      {/* Controls (only for heat map mode) */}
      {mode === "heatmap" && (
        <div className="grid grid-cols-3 gap-4 p-3 bg-muted rounded-lg">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Radius: {controls.radius}px
            </Label>
            <Slider
              value={[controls.radius]}
              onValueChange={([value]) => setControls((prev) => ({ ...prev, radius: value }))}
              min={10}
              max={100}
              step={5}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Blur: {controls.blur}
            </Label>
            <Slider
              value={[controls.blur]}
              onValueChange={([value]) => setControls((prev) => ({ ...prev, blur: value }))}
              min={0}
              max={50}
              step={5}
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">
              Intensity: {controls.intensity}%
            </Label>
            <Slider
              value={[controls.intensity]}
              onValueChange={([value]) => setControls((prev) => ({ ...prev, intensity: value }))}
              min={0}
              max={100}
              step={5}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Image Container */}
      <div 
        ref={containerRef}
        className="relative rounded-lg overflow-hidden border border-border"
      >
        {/* Base Image */}
        <img
          src={imageUrl}
          alt="Heat map visualization"
          className="w-full h-auto"
          onLoad={handleImageLoad}
          draggable={false}
        />

        {/* Heat Map Overlay (aggregate mode) */}
        {mode === "heatmap" && imageLoaded && (
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ mixBlendMode: "multiply" }}
          />
        )}

        {/* Click Points (individual mode) */}
        {/* APPROVED HARDCODED COLOR: #e88463 (coral) for click dots - brand-specific visualization */}
        {mode === "dots" && imageLoaded && (
          <>
            {displayClicks.map((click, index) => (
              <div
                key={`${click.slot}-${index}`}
                className="absolute w-4 h-4 rounded-full border-2 border-white transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  left: `${click.xPct}%`,
                  top: `${click.yPct}%`,
                  backgroundColor: '#e88463',  // Coral - approved hardcoded color for heat map dots
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
                title={`Click ${click.slot}: (${click.xPx}, ${click.yPx})${click.regionId ? ` - ${getRegionName(click.regionId)}` : ""}`}
              >
                {displayClicks.length > 1 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white">
                    {click.slot}
                  </span>
                )}
              </div>
            ))}
          </>
        )}

        {/* Defined Regions Overlay */}
        {config.regions && config.regions.length > 0 && mode !== "regions" && (
          <>
            {config.regions.map((region) => {
              if (region.shape === "rect") {
                return (
                  <div
                    key={region.id}
                    className={cn(
                      "absolute border-2 rounded-sm transition-colors",
                      "border-muted-foreground/50 bg-muted/10"
                    )}
                    style={{
                      left: `${region.xPct}%`,
                      top: `${region.yPct}%`,
                      width: `${region.wPct}%`,
                      height: `${region.hPct}%`,
                    }}
                  >
                    <span className="absolute -top-6 left-0 text-xs bg-background/90 px-1 rounded">
                      {region.name}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </>
        )}

        {/* Empty state */}
        {displayClicks.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="bg-card/90 px-4 py-2 rounded-lg shadow text-sm text-muted-foreground">
              No clicks recorded yet
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {mode === "heatmap" && (
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500" />
            <span className="text-xs text-muted-foreground">Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span className="text-xs text-muted-foreground">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500" />
            <span className="text-xs text-muted-foreground">High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500" />
            <span className="text-xs text-muted-foreground">Very High</span>
          </div>
        </div>
      )}

      {/* Click Stats */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold">{displayClicks.length}</p>
          <p className="text-xs text-muted-foreground">Total Clicks</p>
        </div>
        <div>
          <p className="text-2xl font-bold">{individualResponses.length || 1}</p>
          <p className="text-xs text-muted-foreground">Respondents</p>
        </div>
        <div>
          <p className="text-2xl font-bold">
            {individualResponses.length > 0 
              ? (clicks.length / individualResponses.length).toFixed(1)
              : displayClicks.length.toFixed(1)
            }
          </p>
          <p className="text-xs text-muted-foreground">Avg Clicks/User</p>
        </div>
      </div>

      {/* Region Breakdown (only in regions mode) */}
      {mode === "regions" && hasRegions && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">
            Click Distribution by Region
            {viewMode === "individual" && currentResponse && (
              <span className="text-xs text-muted-foreground ml-2">(Response #{currentResponse.responseIndex})</span>
            )}
          </h4>
          <div className="space-y-2">
            {Array.from(regionCounts.entries()).map(([regionId, count]) => {
              const total = displayClicks.length;
              const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
              
              return (
                <div key={regionId} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground">{getRegionName(regionId)}</span>
                    <span className="text-muted-foreground">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${regionId === "other" ? "bg-muted-foreground" : "bg-primary"} transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default HeatMapVisualization;
